/**
 * Générateur — Automatisme : quotient et reste d'une division euclidienne
 * Attendu ID : 1040 | Niveau : 5ème | Domaine : Opérations
 * Rubrique : AUTOMATISME (programme 2025) — « savoir que 17 = 3 × 5 + 2 ».
 * Labels (cartes exerciseur) :
 *   - Quotient et reste       (égalités à trous + formulation verbale)
 *   - La règle du reste       (QCM vocabulaire + V/F définition)
 *
 * ── Plan de cartes validé (2026-08-30) ─────────────────────────────────────
 * Source : Automatisme n°2 du doc 5e. Le cœur : la relation a = b × q + r
 * avec 0 ≤ r < b, déterminée MENTALEMENT par les tables. BORNES (arbitrage
 * prof) : b ∈ 2..10, q ∈ 3..12, a ≤ 100 : jamais de division par un grand
 * nombre en flash, jamais de virgule, jamais de division posée, jamais de
 * contexte habillé (l'exercice des œufs reste aux évals). INVARIANTS :
 *   - chaque division émise est RE-VÉRIFIÉE (a = b×q + r, 0 ≤ r < b,
 *     a ≤ 100) : throw plutôt qu'un exercice faux ;
 *   - trous : consigne de série qui ÉNONCE LA DÉFINITION (« le reste doit
 *     être plus petit que le diviseur ») : cohérence verticale avec le V/F
 *     et la question « reste = 6 dans une division par 6 » : elle tue les
 *     décompositions non euclidiennes (17 = 3 × 4 + 5) sans convention
 *     implicite ; r ≥ 1 dans les trous (pas de « + 0 » affiché) ;
 *   - chaque série de trous embarque UNE ligne FORMATRICE (pile ou face) :
 *     q ∈ {11, 12} (hors tables strictes : 100 = 9 × 11 + 1) ou r ∈ {1, 2}
 *     avec b ≥ 6 (le dividende frôle un multiple : 50 = 7 × 7 + 1) : la
 *     procédure « encadrer par les multiples » est réellement sollicitée ;
 *   - anti-pattern : jamais trois « r = b − 1 » dans une même série ;
 *   - PAIRE {{q}} + {{r}} : exception commutative SAINE (revue 2026-08-30,
 *     validée prof) : la multiplication est prioritaire, les positions ne
 *     sont pas symétriques : l'échange donne b×r + q ≠ a dès que q ≠ r
 *     ((b−1)(q−r) ≠ 0, mesuré 0/15000), et q = r rend l'ordre indifférent ;
 *   - verbale : reste nul ~1 tirage sur 6 (le lien multiple), formulation
 *     neutre, bonne réponse '0' en saisie standard ;
 *   - QCM vocabulaire : l'énoncé ANCRE « de a par b » (l'égalité nue est
 *     ambiguë : 38 = 5 × 7 + 3 est aussi la division de 38 par 7) et les
 *     quatre nombres {a, b, q, r} sont DEUX À DEUX DISTINCTS (q = b ferait
 *     fondre deux options : 39 = 6 × 6 + 3) ;
 *   - V/F : ~40 % des tirages portent le COUPLE APPARIÉ « reste = b ? (F) /
 *     reste = b − 1 ? (V) » sur le même b, côte à côte : la juxtaposition
 *     fait le clic pédagogique.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1040;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DIVISION = 'Quotient et reste';
const L_REGLE = 'La règle du reste';

// ─── Le domaine ────────────────────────────────────────────────────────────

/** AUTO-VÉRIFICATION : la division émise est euclidienne et dans les bornes. */
const verifier = ({ a, b, q, r }) => {
    if (a !== b * q + r || r < 0 || r >= b || a > 100 || b < 2 || b > 10) {
        throw new Error(`gen_1040 : division invalide ${a} = ${b} × ${q} + ${r}`);
    }
    return { a, b, q, r };
};

/**
 * Diviseur PONDÉRÉ par le nombre de restes qu'il offre (b − 1) : tiré à
 * égalité de chances, « par 2 » (un seul reste possible) accumulait les
 * restes 1-2 à 65 % des lignes ; pondéré, ils retombent à ~56 % (mesuré).
 * Le résiduel est ASSUMÉ (décision prof 2026-08-31) : avec des diviseurs
 * ≤ 10, les petits restes sont la nature du flash : ne pas « uniformiser »
 * davantage (le tirage reste-d'abord a été proposé et écarté). Lignes
 * libres seulement : formatrices et reste nul gardent leur tirage propre.
 */
const diviseurPondere = () => {
    let t = randInt(1, 45); // 1 + 2 + … + 9
    for (let b = 2; b <= 10; b++) {
        t -= b - 1;
        if (t <= 0) return b;
    }
    return 10;
};

/**
 * Tire une division euclidienne du périmètre.
 * mode : 'libre' (r ∈ 1..b−1, diviseur pondéré) | 'formatrice_q'
 *        (q ∈ {11, 12}) | 'formatrice_r' (r ∈ {1, 2}, b ≥ 6) |
 *        'reste_nul' (r = 0).
 */
const tirerDivision = (mode = 'libre') => {
    for (let essai = 0; essai < 80; essai++) {
        let b; let q; let r;
        if (mode === 'formatrice_q') {
            q = randomChoice([11, 12]);
            b = randInt(2, Math.floor(99 / q));
            r = randInt(1, b - 1);
        } else if (mode === 'formatrice_r') {
            // q ≤ 10 aussi : cette formatrice travaille le reste qui frôle,
            // le quotient hors-tables est réservé à formatrice_q.
            b = randInt(6, 10);
            q = randInt(3, Math.min(10, Math.floor(99 / b)));
            r = randInt(1, 2);
        } else if (mode === 'reste_nul') {
            // q ≤ 10 : le tirage courant reste dans les tables, le
            // hors-tables (11, 12) est l'exclusivité de la ligne formatrice.
            b = randInt(2, 10);
            q = randInt(3, Math.min(10, Math.floor(100 / b)));
            r = 0;
        } else {
            b = diviseurPondere();
            q = randInt(3, Math.min(10, Math.floor(99 / b)));
            r = randInt(1, b - 1);
        }
        const a = b * q + r;
        if (a > 100) continue;
        return verifier({ a, b, q, r });
    }
    // Repli déterministe : les exemples du doc, selon le mode.
    if (mode === 'formatrice_q') return verifier({ a: 100, b: 9, q: 11, r: 1 });
    if (mode === 'reste_nul') return verifier({ a: 24, b: 4, q: 6, r: 0 });
    return verifier({ a: 50, b: 7, q: 7, r: 1 });
};

// ─── Carte 1, format A : égalités à trous (saisie) ────────────────────────

const genDivisionTrous = () => {
    // 1 ligne FORMATRICE + 2 libres, diviseurs tous distincts. Le hors-tables
    // (q ∈ {11, 12}) ne sort que par la formatrice, UNE série sur quatre : le
    // gros du flash reste dans les tables (dosage prof 2026-08-30, calé sur
    // la série source : un item hors-tables sur cinq).
    const formatrice = tirerDivision(randInt(1, 4) === 1 ? 'formatrice_q' : 'formatrice_r');
    const lignes = [formatrice];
    for (let essai = 0; essai < 60 && lignes.length < 3; essai++) {
        const d = tirerDivision('libre');
        if (lignes.some((x) => x.b === d.b)) continue;
        lignes.push(d);
    }
    while (lignes.length < 3) {
        // Repli : diviseurs encore libres, petit quotient sûr.
        const b = [2, 3, 4, 5, 6, 7, 8, 9, 10].find((x) => !lignes.some((l) => l.b === x));
        lignes.push(verifier({ a: b * 5 + 1, b, q: 5, r: 1 }));
    }
    // Anti-patterns : jamais trois restes IDENTIQUES (après deux restes
    // égaux, le troisième se devine) ni trois « r = b − 1 » (le motif du
    // reste maximal partout). On corrige une ligne LIBRE (jamais la
    // formatrice, index 0) avec b ≥ 3 : il en existe toujours une
    // (diviseurs distincts, un seul peut valoir 2).
    const troisIdentiques = lignes.every((x) => x.r === lignes[0].r);
    const troisRMax = lignes.every((x) => x.r === x.b - 1);
    if (troisIdentiques || troisRMax) {
        const i = lignes.findIndex((x, idx) => idx > 0 && x.b >= 3);
        const cible = lignes[i];
        const autres = lignes.filter((_, idx) => idx !== i).map((x) => x.r);
        // r = b − 1 n'est exclu QUE s'il recréerait le motif complet (les
        // deux autres lignes déjà à leur reste maximal) : l'exclure toujours
        // viderait les candidats pour b = 3 (mesuré : 57 fuites / 5000).
        const autresRMax = lignes.every((x, idx) => idx === i || x.r === x.b - 1);
        const candidats = [];
        for (let r = 1; r < cible.b; r++) {
            if (autres.includes(r)) continue;
            if (autresRMax && r === cible.b - 1) continue;
            if (cible.b * cible.q + r > 100) continue;
            candidats.push(r);
        }
        const r = candidats.length > 0 ? randomChoice(candidats) : 1;
        lignes[i] = verifier({ a: cible.b * cible.q + r, b: cible.b, q: cible.q, r });
    }
    const melange = shuffle(lignes);
    const phrase = melange.map((x) => `$${x.a} = ${x.b} \\times {{${x.q}}} + {{${x.r}}}$`).join('\n');
    const payload = makeTexteTrou(
        phrase,
        '<p><b>Complète chaque division euclidienne (le reste doit être plus petit que le diviseur).</b></p>',
        { ...opts(L_DIVISION), mode: 'saisie' },
    );
    payload._meta.params = { lignes: melange, formatrice: { b: formatrice.b, q: formatrice.q, r: formatrice.r } };
    return payload;
};

// ─── Carte 1, format B : formulation verbale (saisie) ─────────────────────

const genDivisionVerbale = () => {
    // Reste nul ~1 tirage sur 6 : le lien « reste 0 = multiple » circule.
    const division = tirerDivision(randInt(1, 6) === 1 ? 'reste_nul' : 'libre');
    const role = randomChoice(['reste', 'quotient']);
    const attendu = role === 'reste' ? division.r : division.q;
    const payload = makeSaisie(
        String(attendu),
        `<p><b>Quel est le ${role} de la division euclidienne de ${division.a} par ${division.b} ?</b></p>`,
        opts(L_DIVISION),
    );
    payload._meta.params = { ...division, role };
    return payload;
};

// ─── Carte 2, format A : QCM vocabulaire ──────────────────────────────────

const ROLES = ['dividende', 'diviseur', 'quotient', 'reste'];

const genRegleVocabulaireQcm = () => {
    // {a, b, q, r} deux à deux distincts : aucune option ne doit fondre.
    let division = tirerDivision('libre');
    for (let essai = 0; essai < 60; essai++) {
        const { a, b, q, r } = division;
        if (new Set([a, b, q, r]).size === 4) break;
        division = tirerDivision('libre');
    }
    if (new Set([division.a, division.b, division.q, division.r]).size !== 4) {
        division = verifier({ a: 38, b: 5, q: 7, r: 3 }); // l'exemple du doc
    }
    const { a, b, q, r } = division;
    const role = randomChoice(ROLES);
    const valeurs = { dividende: a, diviseur: b, quotient: q, reste: r };
    const payload = makeQcm(
        [a, b, q, r].map(String),
        String(valeurs[role]),
        `<p><b>On écrit la division euclidienne de ${a} par ${b} :</b></p>`
        + `<p>${tex(`${a} = ${b} \\times ${q} + ${r}`)}</p>`
        + `<p><b>Quel nombre est le ${role} ?</b></p>`,
        opts(L_REGLE),
        false,
    );
    payload._meta.params = {
        ...division,
        role,
        distracteurs: ROLES.filter((x) => x !== role).map((x) => ({ valeur: valeurs[x], code: `confond_avec_${x}` })),
    };
    return payload;
};

// ─── Carte 2, format B : QCM traduction en égalité ────────────────────────

/**
 * « On sait que : quotient q, reste r. Quelle égalité peut-on écrire ? »
 * INTERDIT DE CONSTRUCTION : le distracteur « b et q échangés » (a = q×b+r)
 * est arithmétiquement VRAI par commutativité : un élève qui le cocherait
 * aurait raison (A1 direct). Le vrai piège est l'échange quotient ↔ RESTE.
 * Garanties : q ≠ r (sinon l'échange coïncide avec la bonne), r ≥ 1 (sinon
 * le retranché coïncide), et chaque distracteur RE-VÉRIFIÉ faux (throw).
 */
const genRegleTraductionQcm = () => {
    let d = tirerDivision('libre');
    for (let essai = 0; essai < 60 && d.q === d.r; essai++) d = tirerDivision('libre');
    if (d.q === d.r) d = verifier({ a: 74, b: 8, q: 9, r: 2 });
    const { a, b, q, r } = d;

    const bonne = { texte: tex(`${a} = ${b} \\times ${q} + ${r}`), valeur: b * q + r, code: 'bonne' };
    const distracteurs = [
        { texte: tex(`${a} = ${b} \\times ${r} + ${q}`), valeur: b * r + q, code: 'echange_quotient_reste' },
        { texte: tex(`${a} = ${b} + ${q} + ${r}`), valeur: b + q + r, code: 'tout_additionne' },
        { texte: tex(`${a} = ${b} \\times ${q} - ${r}`), valeur: b * q - r, code: 'reste_retranche' },
    ];
    if (bonne.valeur !== a) throw new Error(`gen_1040 : la bonne égalité est fausse (${a})`);
    distracteurs.forEach((x) => {
        if (x.valeur === a) throw new Error(`gen_1040 : distracteur ${x.code} arithmétiquement vrai (${a})`);
    });

    const payload = makeQcm(
        [bonne.texte, ...distracteurs.map((x) => x.texte)],
        bonne.texte,
        `<p><b>On sait que : dans la division euclidienne de ${a} par ${b}, le quotient est ${q} et le reste est ${r}.</b></p>`
        + '<p><b>Quelle égalité peut-on écrire ?</b></p>',
        opts(L_REGLE),
        false,
    );
    payload._meta.params = { a, b, q, r, distracteurs: distracteurs.map(({ code, valeur }) => ({ code, valeur })) };
    return payload;
};

// ─── Carte 2, format C : V/F définition ───────────────────────────────────

/**
 * Items paramétrés + fixes, chacun porteur d'une clé sémantique (jamais deux
 * fois la même clé dans un tirage, SAUF le couple apparié voulu).
 */
const itemsRegleVf = () => {
    const b = randInt(3, 10);
    const vraieDiv = tirerDivision('libre');
    // Égalité VRAIE arithmétiquement mais NON euclidienne : r' = r + b (donc
    // r' > b strictement) et q' = q − 1 : a = b×q' + r' reste vraie, a ≤ 100.
    let fausse = null;
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerDivision('libre');
        const rPrime = d.r + d.b; // r' ≥ b : jamais un reste euclidien
        const qPrime = d.q - 1;   // a = b(q−1) + (r+b) : l'égalité reste vraie
        if (qPrime < 2) continue;
        const aa = d.b * qPrime + rPrime;
        if (aa !== d.a || aa > 100) continue;
        fausse = { a: d.a, b: d.b, q: qPrime, r: rPrime };
        break;
    }
    // `explication` : le retour pédagogique post-réponse de chaque ligne,
    // embarqué DORMANT dans contenu.explications (pas d'aide avant la
    // réponse : ce format n'a pas de coup de pouce, précédent gen_640 ;
    // l'affichage du feedback est un chantier UI séparé, arbitrage prof
    // 2026-08-31 : la distinction indice/retour).
    const pool = [
        { cle: 'reste_egal_b', texte: `Dans une division par ${b}, le reste peut être égal à ${b}.`, vrai: false, explication: `Dans une division par ${b}, le reste va de 0 à ${b - 1} : il est toujours plus petit que le diviseur.` },
        { cle: 'reste_egal_bm1', texte: `Dans une division par ${b}, le reste peut être égal à ${b - 1}.`, vrai: true, explication: `Le reste peut prendre toutes les valeurs de 0 à ${b - 1} : ${b - 1} est permis, ${b} ne l'est pas.` },
        { cle: 'definition', texte: 'Le reste est toujours plus petit que le diviseur.', vrai: true, explication: 'C\'est la définition : si le reste atteignait le diviseur, on pourrait encore le diviser une fois.' },
        { cle: 'reste_nul', texte: 'Si le reste est 0, le dividende est un multiple du diviseur.', vrai: true, explication: 'Un reste nul signifie que la division tombe juste : le dividende est un multiple du diviseur.' },
        { cle: 'egalite_vraie', texte: `${tex(`${vraieDiv.a} = ${vraieDiv.b} \\times ${vraieDiv.q} + ${vraieDiv.r}`)} est la division euclidienne de ${vraieDiv.a} par ${vraieDiv.b}.`, vrai: true, explication: `L'égalité est vraie et le reste (${vraieDiv.r}) est plus petit que le diviseur (${vraieDiv.b}) : c'est bien la division euclidienne.` },
    ];
    if (fausse) {
        pool.push({ cle: 'egalite_non_euclidienne', texte: `${tex(`${fausse.a} = ${fausse.b} \\times ${fausse.q} + ${fausse.r}`)} est la division euclidienne de ${fausse.a} par ${fausse.b}.`, vrai: false, explication: `L'égalité est vraie, mais le reste écrit (${fausse.r}) dépasse le diviseur (${fausse.b}) : ce n'est pas la division euclidienne.` });
    }
    return { pool, b };
};

const genRegleVf = () => {
    const { pool } = itemsRegleVf();
    let tirage;
    if (randInt(1, 10) <= 4) {
        // ~40 % : le COUPLE APPARIÉ sur le même b, côte à côte, + une tierce.
        const paire = [pool.find((x) => x.cle === 'reste_egal_b'), pool.find((x) => x.cle === 'reste_egal_bm1')];
        const tierce = randomChoice(pool.filter((x) => !x.cle.startsWith('reste_egal_')));
        tirage = [...paire, tierce];
    } else {
        // Standard : mixte, clés distinctes, et JAMAIS le couple apparié
        // (il n'existe que par la branche délibérée, sinon il déborde son
        // ~40 % : mesuré à 55 % avant cette garde).
        const valide = (t) => {
            const vrais = t.filter((x) => x.vrai).length;
            const cles = new Set(t.map((x) => x.cle));
            return vrais >= 1 && vrais <= 2 && cles.size === 3
                && !(cles.has('reste_egal_b') && cles.has('reste_egal_bm1'));
        };
        tirage = shuffle(pool).slice(0, 3);
        for (let essai = 0; essai < 40 && !valide(tirage); essai++) {
            tirage = shuffle(pool).slice(0, 3);
        }
        if (!valide(tirage)) {
            // Repli déterministe : trio mixte sans couple.
            tirage = [
                pool.find((x) => x.cle === 'definition'),
                pool.find((x) => x.cle === 'reste_egal_b'),
                pool.find((x) => x.cle === 'reste_nul'),
            ];
        }
    }
    const payload = makeVraiFaux(
        tirage.map((x) => x.texte),
        tirage.map((x) => (x.vrai ? 0 : 1)),
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L_REGLE),
    );
    // Retour pédagogique par ligne, DORMANT (cf. commentaire du pool).
    payload.contenu = { ...payload.contenu, explications: tirage.map((x) => x.explication) };
    payload._meta.params = { cles: tirage.map((x) => x.cle) };
    return payload;
};

// ─── Contrat distracteurs (référence de revue, format QCM) ────────────────

/**
 * QCM vocabulaire : les quatre choix sont PAR NATURE les quatre nombres de
 * l'égalité affichée : chaque distracteur EST un autre rôle (code
 * confond_avec_<rôle>), zéro bouche-trou possible. Arbitrage prof 2026-08-30.
 */
export const REGLES_DISTRACTEURS_QCM = {
    regle_traduction_qcm: {
        support: 'les données en toutes lettres (quotient, reste), 4 égalités en choix : UNE SEULE vraie, re-vérifiée',
        interdit: 'a = q × b + r (diviseur et quotient échangés) : VRAI par commutativité, jamais en distracteur',
        fixes: {
            echange_quotient_reste: 'a = b × r + q : le reste passe en facteur, le quotient en terme ajouté (garanti faux par q ≠ r)',
            tout_additionne: 'a = b + q + r : les trois nombres empilés en somme (garanti faux : b × q > b + q)',
            reste_retranche: 'a = b × q − r : le reste se retire au lieu de s\'ajouter (garanti faux par r ≥ 1)',
        },
    },
    regle_vocabulaire_qcm: {
        support: 'une division euclidienne ancrée « de a par b », les 4 nombres deux à deux distincts',
        fixes: {
            confond_avec_dividende: 'répond a : confond le rôle demandé avec le nombre divisé',
            confond_avec_diviseur: 'répond b : confond quotient et diviseur (les deux facteurs du produit)',
            confond_avec_quotient: 'répond q : confond reste et quotient (les deux résultats de la division)',
            confond_avec_reste: 'répond r : confond quotient et reste',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    division_texte_trou_saisie: genDivisionTrous,
    division_verbale_saisie: genDivisionVerbale,
    regle_vocabulaire_qcm: genRegleVocabulaireQcm,
    regle_traduction_qcm: genRegleTraductionQcm,
    regle_vf: genRegleVf,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', verbale_saisie: 'Saisie',
    vocabulaire_qcm: 'QCM', traduction_qcm: 'QCM égalité', vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'division', label: L_DIVISION, formatsList: buildFormats('division') },
    { id: 'regle', label: L_REGLE, formatsList: buildFormats('regle') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
